<template>
    <Dialog v-model:visible="editDialogVisible" modal header="Edit paper details" class="papers-dialog"
        :style="{ width: '38rem', maxWidth: '95vw' }">
        <div class="field-grid dialog-grid">
            <div class="field field-wide">
                <label class="field-label">Paper name</label>
                <InputText v-model="editForm.name" class="w-full" size="small" variant="filled" />
            </div>

            <div class="field">
                <label class="field-label">Grade</label>
                <Select v-model="editForm.grade" :options="gradeOptions" optionLabel="name"
                    placeholder="Select grade" class="w-full" size="small" variant="filled"
                    @change="onEditGradeChange" />
            </div>

            <div class="field">
                <label class="field-label">Subject</label>
                <Select v-model="editForm.subject" :options="editSubjects" optionLabel="name"
                    :disabled="!editForm.grade" placeholder="Select subject" class="w-full" size="small"
                    variant="filled" filter />
            </div>

            <div class="field">
                <label class="field-label">
                    Year <span class="optional-hint">optional</span>
                </label>
                <Select v-model="editForm.year" :options="yearChoices" placeholder="Select year" class="w-full"
                    size="small" variant="filled" filter show-clear />
            </div>

            <div class="field" v-if="editMediumApplies">
                <label class="field-label">
                    Medium <span class="optional-hint">optional</span>
                </label>
                <Select v-model="editForm.medium" :options="mediumChoices" optionLabel="label" optionValue="value"
                    placeholder="Select medium" class="w-full" size="small" variant="filled" show-clear />
            </div>

            <div class="field" :class="{ 'field-wide': !editMediumApplies }">
                <label class="field-label">
                    Term <span class="optional-hint">optional</span>
                </label>
                <Select v-model="editForm.term" :options="termList" optionLabel="name" optionValue="code"
                    placeholder="Select term" class="w-full" size="small" variant="filled" show-clear />
            </div>

            <div class="field field-wide">
                <label class="field-label">Status</label>
                <Select v-model="editForm.status" :options="statusChoices" optionLabel="label" optionValue="value"
                    class="w-full" size="small" variant="filled" />
                <small class="field-hint">
                    <i class="pi pi-eye-slash" />Archived papers are hidden from students and tutors.
                </small>
            </div>
        </div>

        <template #footer>
            <Button label="Cancel" size="small" class="ghost-button" @click="editDialogVisible = false" />
            <Button label="Save changes" icon="pi pi-check" size="small" class="accent-button"
                :loading="isSavingEdit" @click="saveEdit" />
        </template>
    </Dialog>
</template>

<script setup>
import { ref, reactive, computed, watch, defineProps, defineEmits } from 'vue'
import { Button, Select } from 'primevue'
import InputText from 'primevue/inputtext';
import Dialog from 'primevue/dialog';
import { notifyError, notifySuccess, notifyWarn } from '@/utils/notification';
import { updatePaper } from '@/api/papers';
import { isLocalSyllabusGrade, PAPER_MEDIUM_OPTIONS } from '@/utils/paperFiles';
import { termList, gradeOptions } from '@/utils/options';

const props = defineProps({
    visible: { type: Boolean, default: false },
    paper: { type: Object, default: null },
});

const emit = defineEmits(['update:visible', 'saved']);

const editDialogVisible = computed({
    get: () => props.visible,
    set: (value) => emit('update:visible', value),
});

const mediumChoices = PAPER_MEDIUM_OPTIONS;

const statusChoices = [
    { label: 'Active', value: 'active' },
    { label: 'Archived', value: 'archived' },
];

const isSavingEdit = ref(false);
const editForm = reactive({
    id: null, name: '', grade: null, subject: null, year: null, medium: null, term: null, status: 'active',
});

const yearChoices = computed(() => {
    const current = new Date().getFullYear();
    return Array.from({ length: 40 }, (_, i) => current - i);
});

const editSubjects = computed(() => editForm.grade?.subjects || []);
const editMediumApplies = computed(() => !editForm.grade || isLocalSyllabusGrade(editForm.grade.name));

watch(() => props.visible, (visible) => {
    if (!visible || !props.paper) return;

    const paper = props.paper;
    const grade = gradeOptions.value?.find(g => g.code === paper.gradeCode) || null;
    editForm.id = paper._id;
    editForm.name = paper.name;
    editForm.grade = grade;
    editForm.subject = grade?.subjects?.find(s => s.code === paper.subjectCode) || null;
    editForm.year = paper.year;
    editForm.medium = paper.medium;
    editForm.term = paper.term;
    editForm.status = paper.status;
});

const onEditGradeChange = () => {
    editForm.subject = null;
    if (!editMediumApplies.value) editForm.medium = null;
};

const saveEdit = async () => {
    if (!editForm.name.trim() || !editForm.grade || !editForm.subject) {
        notifyWarn("Missing details", "Name, grade and subject are required.");
        return;
    }

    isSavingEdit.value = true;
    try {
        await updatePaper(editForm.id, {
            name: editForm.name.trim(),
            grade: editForm.grade.code,
            subject: editForm.subject.code,
            year: editForm.year || null,
            medium: editMediumApplies.value ? editForm.medium || null : null,
            term: editForm.term || null,
            status: editForm.status,
        });
        notifySuccess("Updated!", "Paper details saved.");
        editDialogVisible.value = false;
        emit('saved');
    } catch (err) {
        notifyError("Error", err?.response?.data?.message || "Failed to update the paper.");
    } finally {
        isSavingEdit.value = false;
    }
};
</script>

<style scoped>
.field-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.9rem 1rem;
}

.field-wide {
    grid-column: 1 / -1;
}

@media (max-width: 640px) {
    .field-grid {
        grid-template-columns: minmax(0, 1fr);
    }

    .field-wide {
        grid-column: auto;
    }
}

.field {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    min-width: 0;
}

.field-label {
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--gray-600);
}

.optional-hint {
    margin-left: 0.15rem;
    padding: 0.05rem 0.35rem;
    border-radius: 999px;
    background-color: var(--gray-200);
    font-size: 0.65rem;
    font-weight: 400;
    color: var(--black-700);
}

.field-hint {
    display: flex;
    align-items: center;
    gap: 0.3rem;
    font-size: 0.7rem;
    line-height: 1.4;
    color: var(--black-700);
}

.field-hint i {
    font-size: 0.68rem;
}

.field :deep(.p-select) {
    width: 100%;
}

.ghost-button.p-button {
    height: 32px;
    padding: 8px 16px;
    border-radius: 4px;
    border: 1px solid var(--black-50);
    background-color: #fff;
    color: var(--gray-600);
    font-size: 14px;
    transition: background-color 0.2s, border-color 0.2s;
}

.ghost-button.p-button:not(:disabled):hover {
    background-color: var(--gray-200);
    border-color: var(--black-50);
    color: var(--gray-600);
}

.ghost-button.p-button:focus,
.ghost-button.p-button:active {
    border-color: transparent;
    outline: none;
    box-shadow: none;
}

.ghost-button.p-button:hover {
    outline: none;
    box-shadow: none;
}

.ghost-button.p-button:focus-visible {
    outline: 1px solid var(--black-700);
    outline-offset: 1px;
    box-shadow: none;
}
</style>
